import { isNil } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';

import useAccount from '~hooks/useAccount/useAccount';
import type { Order } from '~hooks/useOrders/models';
import type { Project } from '~hooks/useProjects/models';
import { toTaxRateScheduleFormValue } from '~pages/Sales/Projects/projectFormSchema';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * The main project-change cascade — the value-copy tail of the legacy
 * project-change effect plus the project-metadata effects that ride the same
 * entity. The entity FETCH itself is useProjectDataFetch; this module reacts
 * to the `projectData` it publishes, which changes identity exactly once per
 * fetch resolution — the same cadence as legacy running the copies inside
 * the fetch callback.
 *
 * Triggers: `projectData` (per fetch resolution); the entity's metadata
 *           projections (account/department/notes/contract/dispatch/PO);
 *           `phase` (watched); the entity's projectMaterialTotals/phases
 *           projections; `account.id` (watched).
 * Writes:   new-order defaults: haulerRateType, truckCount,
 *           quantity, unitOfMeasure, equipmentTypeGrossCapacity(+UoM),
 *           jobQuantity, jobUnitOfMeasure, recurringLoads,
 *           chaseMissingTickets, liveEta, defaultDigitalTicketView,
 *           prevailingWage, materialRateItemized,
 *           and the tax block (taxRateSchedule/taxRate/taxFreight/
 *           taxMaterial/taxAddOns + the Provider's isTaxSectionVisible);
 *           project metadata: projectExternalId, account,
 *           department, projectNotes, orderNotes, jobNotes, internalNotes,
 *           contractNumber, dispatchNumber, poJobNumber; phaseCode;
 *           projectMaterialTotals; allPhaseCodes; the account's default
 *           department; the Provider's shared `loaded` latch (the
 *           site-contact effects read the same latch). The orderNotes/
 *           jobNotes/internalNotes/contractNumber/dispatchNumber/poJobNumber
 *           copies dirty when a project CHANGE drove them, and the
 *           account-default department dirties too: deparseUpdateV3 gates
 *           every key on dirtyFields, so an undirtied copy renders on the
 *           edit form while the PATCH keeps the previous project's values.
 * Guards:   the new-order defaults run only while `!defaultOrder?.orderId`
 *           (a persisted order keeps its saved values; copy-and-edit keeps
 *           the source's); each metadata write keeps the mounted order's own
 *           value when present (per-field `!defaultOrder?.X` guards; the
 *           account/department seeds additionally require an id on the
 *           project side); the department-default fetch runs only on a live
 *           account change after mount (`loaded`) while no department is
 *           selected, or on a Copy & Edit while the source department is
 *           still selected, where it replaces (or clears) it, and switching
 *           back to the source account restores it; the phaseCode mirror only
 *           writes a non-empty code, so it never erases the code
 *           ProjectDetailsV2 wrote from the phase widget. Dirtying is
 *           per-write: the metadata copies ride a
 *           previous-project-id ref seeded with the mounted order's project
 *           that advances only on a resolved entity, so the mount landing of
 *           that same project stays pristine, and RHF records a dirty bit
 *           only where the copied value differs from the loaded one; the
 *           department default rides the `loaded` latch, which already limits
 *           it to post-mount account changes; every other write declares
 *           `{ shouldDirty: false }`, either because its key is not deparsed on
 *           update or because the new-order guard means the create body carries
 *           it regardless.
 * Legacy:   the legacy form's project-change cascade — new-order defaults,
 *           project metadata, phaseCode/totals/phases, and the department
 *           default (including the shared `loaded` latch), all undirtied
 *           there because its full PATCH shipped every key regardless.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("project-change cascade").
 */
export function useProjectCascade({
  control,
  setValue,
  defaultOrder,
  isCopyAndEdit,
  projectData,
  watchedProjectExternalId,
  loaded,
  setLoaded,
  setIsTaxSectionVisible,
  itemizeMaterialRateDefaultOn,
}: {
  control: Control<OrderFormV3FieldValues>;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
  defaultOrder: Order | null;
  isCopyAndEdit: boolean;
  projectData: Project | null;
  watchedProjectExternalId: string | undefined;
  loaded: boolean;
  setLoaded: (loaded: boolean) => void;
  setIsTaxSectionVisible: (visible: boolean) => void;
  itemizeMaterialRateDefaultOn: boolean;
}): void {
  const { getAccountByIdString } = useAccount();

  // The metadata projections the effects below key on. Project getters
  // return stored references, so each projection is identity-stable until a
  // new entity instance lands.
  const projectPhases = projectData?.phases;
  const projectMaterialTotals = projectData?.projectMaterialTotals;
  const projectNotes = projectData?.notes;
  const projectOrderNotes = projectData?.orderNotes;
  const projectJobNotes = projectData?.jobNotes;
  const projectInternalNotes = projectData?.internalNotes;
  const projectAccount = projectData?.account;
  const projectDepartment = projectData?.department;
  const projectDispatchNumber = projectData?.dispatchNumber;
  const projectContractNumber = projectData?.contractNumber;
  const projectPoJobNumber = projectData?.poJobNumber;

  // A freshly fetched entity seeds the order defaults on NEW orders only.
  // Each copy keeps legacy's own existence guard so an empty project field
  // never clobbers a user-visible default.
  useEffect(() => {
    if (!projectData || defaultOrder?.orderId) {
      return;
    }

    if (projectData.haulerRateType) {
      setValue(
        'haulerRateType',
        {
          label: getRateTypeLabel(projectData.haulerRateType),
          value: projectData.haulerRateType,
        },
        { shouldDirty: false },
      );
    }

    if (projectData.truckCount) {
      setValue('truckCount', projectData.truckCount, { shouldDirty: false });
    }

    if (projectData.orderQuantity) {
      setValue('quantity', projectData.orderQuantity, { shouldDirty: false });
    }

    if (projectData.orderUnitOfMeasure) {
      setValue('unitOfMeasure', projectData.orderUnitOfMeasure, { shouldDirty: false });
    }

    if (projectData.equipmentTypeGrossCapacity) {
      setValue('equipmentTypeGrossCapacity', projectData.equipmentTypeGrossCapacity, {
        shouldDirty: false,
      });
    }

    if (projectData.equipmentTypeGrossCapacityUnitOfMeasure) {
      setValue(
        'equipmentTypeGrossCapacityUnitOfMeasure',
        projectData.equipmentTypeGrossCapacityUnitOfMeasure,
        { shouldDirty: false },
      );
    }

    if (projectData.jobQuantity) {
      setValue('jobQuantity', projectData.jobQuantity, { shouldDirty: false });
    }

    if (projectData.jobUnitOfMeasure) {
      setValue('jobUnitOfMeasure', projectData.jobUnitOfMeasure, { shouldDirty: false });
    }

    if (!isNil(projectData.recurringLoads)) {
      setValue('recurringLoads', projectData.recurringLoads, { shouldDirty: false });
    }

    if (!isNil(projectData.chaseMissingTickets)) {
      setValue('chaseMissingTickets', projectData.chaseMissingTickets, {
        shouldDirty: false,
      });
    }

    setValue('liveEta', projectData.liveEta, { shouldDirty: false });

    if (!isNil(projectData.defaultDigitalTicketView)) {
      setValue('defaultDigitalTicketView', projectData.defaultDigitalTicketView, {
        shouldDirty: false,
      });
    }

    setValue('prevailingWage', projectData.prevailingWage ?? false, {
      shouldDirty: false,
    });

    setValue(
      'materialRateItemized',
      projectData.materialRateItemized ?? itemizeMaterialRateDefaultOn,
      { shouldDirty: false },
    );

    if (!isNil(projectData.taxRate) || projectData.taxRateSchedule) {
      if (projectData.taxRateSchedule) {
        setValue(
          'taxRateSchedule',
          toTaxRateScheduleFormValue(projectData.taxRateSchedule),
          { shouldDirty: false },
        );
      }
      setValue('taxRate', projectData.taxRate ?? null, { shouldDirty: false });
      setValue('taxFreight', projectData.taxFreight ?? false, { shouldDirty: false });
      setValue('taxMaterial', projectData.taxMaterial ?? false, { shouldDirty: false });
      setValue('taxAddOns', projectData.taxAddOns ?? false, { shouldDirty: false });
      setIsTaxSectionVisible(true);
    }
    // One run per fetched entity — defaultOrder/setters are read via closure,
    // exactly as legacy reads them inside the fetch callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData]);

  // Project metadata + notes, with per-field guards that keep the mounted
  // order's own values.
  const previousCascadedProjectIdRef = useRef(defaultOrder?.project?.id);
  useEffect(() => {
    const cascadedProjectId = projectData?.id;
    const isLiveProjectChange =
      Boolean(cascadedProjectId) &&
      cascadedProjectId !== previousCascadedProjectIdRef.current;

    if (cascadedProjectId) {
      previousCascadedProjectIdRef.current = cascadedProjectId;
    }

    setValue('projectExternalId', watchedProjectExternalId || '', {
      shouldDirty: false,
    });
    if (!defaultOrder?.account?.id?.length && projectAccount?.id?.length) {
      setValue(
        'account',
        {
          id: projectAccount.id,
          name: projectAccount.name,
        },
        { shouldDirty: false },
      );
    }

    // A new order preloads the department from the selected project when none
    // was previously selected.
    if (
      !defaultOrder?.orderId &&
      !defaultOrder?.department?.id?.length &&
      projectDepartment?.id?.length
    ) {
      setValue(
        'department',
        {
          id: projectDepartment.id,
          name: projectDepartment.name,
        },
        { shouldDirty: false },
      );
    }

    setValue('projectNotes', projectNotes || '', { shouldDirty: false });
    if (!defaultOrder?.notes) {
      setValue('orderNotes', projectOrderNotes || '', {
        shouldDirty: isLiveProjectChange,
      });
    }
    if (!defaultOrder?.jobNotes) {
      setValue('jobNotes', projectJobNotes || '', { shouldDirty: isLiveProjectChange });
    }
    if (!defaultOrder?.internalNotes) {
      setValue('internalNotes', projectInternalNotes || '', {
        shouldDirty: isLiveProjectChange,
      });
    }
    if (!defaultOrder?.contractNumber) {
      setValue('contractNumber', projectContractNumber || '', {
        shouldDirty: isLiveProjectChange,
      });
    }
    if (!defaultOrder?.dispatchNumber) {
      setValue('dispatchNumber', projectDispatchNumber?.name || '', {
        shouldDirty: isLiveProjectChange,
      });
    }
    if (!defaultOrder?.poJobNumber) {
      setValue('poJobNumber', projectPoJobNumber || '', {
        shouldDirty: isLiveProjectChange,
      });
    }
    // Legacy deps verbatim — the three secondary note projections and the
    // watched project's externalId ride the same entity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectNotes,
    projectAccount,
    projectContractNumber,
    projectDepartment,
    projectDispatchNumber,
    projectPoJobNumber,
  ]);

  // The selected phase mirrors into the flat phaseCode field the deparser
  // reads. ProjectDetailsV2 writes the same field from the phase widget's own
  // internal form, and `phase` is only populated when the user picks a phase
  // by hand — a phase auto-filled from the project leaves it empty. Writing
  // an empty string here would therefore erase the auto-filled code, leaving
  // the order unsubmittable while the widget still displays the phase.
  const phase = useWatch({ control, name: 'phase' });
  useEffect(() => {
    if (!phase?.code) {
      return;
    }

    setValue('phaseCode', phase.code, { shouldDirty: false });
    // Legacy deps verbatim ([phase]).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // The read-only material-totals rows (rendered by ProjectDetailsPanel)
  // render from this form field.
  useEffect(() => {
    setValue('projectMaterialTotals', projectMaterialTotals ?? [], {
      shouldDirty: false,
    });
    // Legacy deps verbatim ([projectMaterialTotals]).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectMaterialTotals]);

  // The full phase list backs the phase selection.
  useEffect(() => {
    setValue('allPhaseCodes', projectPhases ?? [], { shouldDirty: false });
    // Legacy deps verbatim ([projectPhases]).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPhases]);

  // A live account change seeds the account's default department while none
  // is selected. The first run after mount only flips the shared `loaded`
  // latch (account ids never fetch at mount), exactly like legacy.
  const account = useWatch({ control, name: 'account' });
  const watchedCompany = useWatch({ control, name: 'company' });
  const watchedDepartment = useWatch({ control, name: 'department' });
  const autoFilledDepartmentIdRef = useRef(defaultOrder?.department?.id);
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const changeDepartmentDefault = async (accountId?: string) => {
      const watchedDepartmentId =
        watchedDepartment &&
        typeof watchedDepartment === 'object' &&
        'id' in watchedDepartment
          ? watchedDepartment.id
          : undefined;

      const sourceAccountId = isCopyAndEdit ? defaultOrder?.account?.id : undefined;
      const sourceDepartment = defaultOrder?.department;

      if (accountId && loaded && accountId === sourceAccountId) {
        setValue('department', sourceDepartment ?? null, { shouldDirty: true });
      } else if (
        accountId &&
        loaded &&
        (sourceAccountId
          ? watchedDepartmentId === sourceDepartment?.id ||
            watchedDepartmentId === autoFilledDepartmentIdRef.current
          : !watchedDepartmentId)
      ) {
        let accountDetails;
        try {
          accountDetails = await getAccountByIdString(
            accountId,
            watchedCompany?.id,
            signal,
          );
        } catch (error) {
          if (signal.aborted) {
            return;
          }

          throw error;
        }

        if (signal.aborted) {
          return;
        }

        if (accountDetails?.department) {
          autoFilledDepartmentIdRef.current = accountDetails.department.id;
          setValue(
            'department',
            {
              id: accountDetails.department.id,
              name: accountDetails.department.name,
            },
            { shouldDirty: true },
          );
        } else if (sourceAccountId) {
          autoFilledDepartmentIdRef.current = undefined;
          setValue('department', null, { shouldDirty: true });
        }
      }

      setLoaded(true);
    };

    void changeDepartmentDefault(account?.id ?? undefined);

    return () => controller.abort();
    // Legacy deps verbatim ([account?.id]) — the department/loaded reads ride
    // the closure of the render that changed the account.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);
}
